import React, { useState, useEffect, useCallback } from 'react';
import './QuizEditor.css';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const getAuthToken = (propToken) => {
  if (propToken) return propToken;
  try {
    const raw = localStorage.getItem('app_auth_session');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.session_token === 'string' ? parsed.session_token.trim() : '';
  } catch { return ''; }
};
const authHeaders = (propToken) => {
  const token = getAuthToken(propToken);
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

// ── Type label map ────────────────────────────────────────────────────────
const TYPE_LABELS = {
  scq: 'Single Choice',
  mcq: 'Multi-Select',
  truefalse: 'True / False',
  match: 'Match Pairs',
  fillblank: 'Fill in the Blank',
  assertion: 'Assertion–Reason',
  ordering: 'Ordering',
};

export default function QuizEditor({ templateId, quizRequest, onClose, onSaved, token }) {
  const [question, setQuestion] = useState(null);
  const [isQbank, setIsQbank] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  const loadQuizData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/lesson/templates/${templateId}/quiz`,
        { credentials: 'include', headers: authHeaders(token) }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const allQuestions = [...(data.kc_questions || []), ...(data.assessment_questions || [])];
      const target = allQuestions.find(q => q.section_id === quizRequest.section_id);

      if (target) {
        if (target.qbank_question) {
          // Qbank-based assessment — use rich question object
          setIsQbank(true);
          setQuestion({ ...target.qbank_question });
        } else {
          // KC or legacy assessment — use flat options format
          setIsQbank(false);
          setQuestion({
            question_text: target.question_text,
            options: target.options.map(o => ({ ...o })),
            interaction_type: target.interaction_type || 'single',
          });
        }
      } else {
        setError(`Question for section "${quizRequest.section_id}" not found`);
      }
    } catch (e) {
      setError(`Failed to load quiz data: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [templateId, quizRequest]);

  useEffect(() => {
    if (!quizRequest || !templateId) return;
    loadQuizData();
  }, [quizRequest, templateId, loadQuizData]);

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!question) return;
    setIsSaving(true);
    setError('');
    setSaveStatus('');

    try {
      if (isQbank) {
        // Qbank assessment — dedicated endpoint
        const res = await fetch(
          `${API_URL}/lesson/templates/${templateId}/assessment-question`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: authHeaders(token),
            body: JSON.stringify({
              assessment_index: quizRequest.assessment_index,
              question,
            }),
          }
        );
        if (!res.ok) { const t = await res.text(); throw new Error(t.slice(0, 200)); }
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Save failed');
      } else {
        // KC or legacy assessment — existing endpoint
        if (!question.question_text?.trim()) { setError('Question text is required'); setIsSaving(false); return; }
        if (question.options.length < 2) { setError('At least 2 options required'); setIsSaving(false); return; }
        if (!question.options.some(o => o.correct)) { setError('At least one option must be correct'); setIsSaving(false); return; }
        if (question.options.some(o => !o.text?.trim())) { setError('All options must have text'); setIsSaving(false); return; }

        const res = await fetch(
          `${API_URL}/lesson/templates/${templateId}/quiz`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: authHeaders(token),
            body: JSON.stringify({
              quiz_type: quizRequest.quiz_type,
              section_id: quizRequest.section_id,
              question_text: question.question_text,
              options: question.options,
              interaction_type: question.interaction_type,
              ...(quizRequest.quiz_type === 'assessment' ? { assessment_index: quizRequest.assessment_index } : {}),
            }),
          }
        );
        if (!res.ok) { const t = await res.text(); throw new Error(t.slice(0, 200)); }
        const data = await res.json();
        const errorNote = data.notes?.find(n => n.toLowerCase().startsWith('error'));
        if (!data.success || errorNote) throw new Error(data.error || errorNote || 'Save failed');
      }

      setSaveStatus('success');
      setTimeout(() => { onSaved?.(); onClose?.(); }, 1000);
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!quizRequest) return null;

  return (
    <div className="quiz-editor-overlay" onClick={onClose}>
      <div className="quiz-editor-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="quiz-editor-header">
          <div className="quiz-editor-title">
            <span className="quiz-type-badge">
              {isQbank && question ? (TYPE_LABELS[question.type] || question.type) : (quizRequest.quiz_type === 'assessment' ? 'Assessment' : 'Knowledge Check')}
            </span>
            <span>{quizRequest.section_id}</span>
          </div>
          <button className="quiz-editor-close" onClick={onClose}>✕</button>
        </div>

        {isLoading ? (
          <div className="quiz-editor-loading">Loading question data...</div>
        ) : (
          <div className="quiz-editor-body">
            {isQbank && question ? (
              <QbankQuestionEditor question={question} setQuestion={setQuestion} />
            ) : (
              <LegacyQuestionEditor question={question} setQuestion={setQuestion} />
            )}

            {error && <div className="qe-error">{error}</div>}
            {saveStatus === 'success' && <div className="qe-success">✓ Saved! Lesson updating...</div>}

            <div className="quiz-editor-actions">
              <button className="qe-btn cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
              <button className="qe-btn save" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Question'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Qbank question editor (type-aware) ────────────────────────────────────
function QbankQuestionEditor({ question, setQuestion }) {
  const qtype = question.type || 'scq';
  const set = (field, val) => setQuestion(prev => ({ ...prev, [field]: val }));

  return (
    <div>
      {/* Stem */}
      <div className="qe-field">
        <label className="qe-label">Question</label>
        <textarea className="qe-textarea" rows={3} value={question.stem || ''}
          onChange={e => set('stem', e.target.value)} placeholder="Question text..." />
      </div>

      {/* Explanation */}
      <div className="qe-field">
        <label className="qe-label">Explanation <span className="qe-hint">(shown after answer)</span></label>
        <textarea className="qe-textarea" rows={2} value={question.explanation || ''}
          onChange={e => set('explanation', e.target.value)} placeholder="Why is this the correct answer?" />
      </div>

      {/* Type-specific fields */}
      {(qtype === 'scq' || qtype === 'truefalse' || qtype === 'assertion') && (
        <OptionsEditor
          options={question.options || []}
          correct={question.correct}
          multi={false}
          onChange={(options, correct) => setQuestion(prev => ({ ...prev, options, correct }))}
        />
      )}

      {qtype === 'mcq' && (
        <OptionsEditor
          options={question.options || []}
          correct={question.correct || []}
          multi={true}
          onChange={(options, correct) => setQuestion(prev => ({ ...prev, options, correct }))}
        />
      )}

      {qtype === 'fillblank' && (
        <div className="qe-field">
          <label className="qe-label">Correct Answer <span className="qe-hint">(exact text for the blank)</span></label>
          <input className="qe-option-input" type="text" value={question.blank || ''}
            onChange={e => set('blank', e.target.value)} placeholder="e.g. photosynthesis" />
        </div>
      )}

      {qtype === 'match' && (
        <PairsEditor
          pairs={question.pairs || []}
          onChange={pairs => set('pairs', pairs)}
        />
      )}

      {qtype === 'ordering' && (
        <OrderingEditor
          options={question.options || []}
          correct={question.correct || []}
          onChange={(options, correct) => setQuestion(prev => ({ ...prev, options, correct }))}
        />
      )}
    </div>
  );
}

// ── Options editor (scq / mcq) ────────────────────────────────────────────
function OptionsEditor({ options, correct, multi, onChange }) {
  const isCorrect = (idx) => multi ? (correct || []).includes(idx) : correct === idx;

  const toggleCorrect = (idx) => {
    if (multi) {
      const arr = correct || [];
      const next = arr.includes(idx) ? arr.filter(i => i !== idx) : [...arr, idx];
      onChange(options, next);
    } else {
      onChange(options, idx);
    }
  };

  const updateText = (idx, text) => {
    onChange(options.map((o, i) => i === idx ? text : o), correct);
  };

  const addOption = () => onChange([...options, ''], correct);

  const removeOption = (idx) => {
    const next = options.filter((_, i) => i !== idx);
    const nextCorrect = multi
      ? (correct || []).filter(i => i !== idx).map(i => i > idx ? i - 1 : i)
      : (correct > idx ? correct - 1 : correct === idx ? null : correct);
    onChange(next, nextCorrect);
  };

  return (
    <div className="qe-field">
      <div className="qe-options-header">
        <label className="qe-label">Options <span className="qe-hint">{multi ? '— check all correct' : '— click to set correct'}</span></label>
        <button className="qe-add-btn" onClick={addOption}>+ Add</button>
      </div>
      <div className="qe-options-list">
        {options.map((opt, idx) => (
          <div key={idx} className={`qe-option-row ${isCorrect(idx) ? 'correct' : ''}`}>
            <button className={`qe-correct-toggle ${isCorrect(idx) ? 'active' : ''}`} onClick={() => toggleCorrect(idx)}>
              {multi ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill={isCorrect(idx) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  {isCorrect(idx) && <polyline points="7 12 11 16 17 8"/>}
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  {isCorrect(idx) && <circle cx="12" cy="12" r="5" fill="currentColor"/>}
                </svg>
              )}
            </button>
            <input className="qe-option-input" type="text" value={opt}
              onChange={e => updateText(idx, e.target.value)} placeholder={`Option ${idx + 1}`} />
            {isCorrect(idx) && <span className="qe-correct-label">✓</span>}
            {options.length > 2 && (
              <button className="qe-remove-btn" onClick={() => removeOption(idx)}>✕</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Pairs editor (match) ──────────────────────────────────────────────────
function PairsEditor({ pairs, onChange }) {
  const updatePair = (idx, side, val) => {
    onChange(pairs.map((p, i) => i === idx ? { ...p, [side]: val } : p));
  };
  const addPair = () => onChange([...pairs, { left: '', right: '' }]);
  const removePair = (idx) => onChange(pairs.filter((_, i) => i !== idx));

  return (
    <div className="qe-field">
      <div className="qe-options-header">
        <label className="qe-label">Pairs <span className="qe-hint">— left matches right</span></label>
        <button className="qe-add-btn" onClick={addPair}>+ Add pair</button>
      </div>
      <div className="qe-options-list">
        {pairs.map((pair, idx) => (
          <div key={idx} className="qe-option-row qe-pair-row">
            <input className="qe-option-input" type="text" value={pair.left || ''}
              onChange={e => updatePair(idx, 'left', e.target.value)} placeholder="Left item" />
            <span className="qe-pair-arrow">→</span>
            <input className="qe-option-input" type="text" value={pair.right || ''}
              onChange={e => updatePair(idx, 'right', e.target.value)} placeholder="Right item" />
            {pairs.length > 2 && (
              <button className="qe-remove-btn" onClick={() => removePair(idx)}>✕</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ordering editor ───────────────────────────────────────────────────────
function OrderingEditor({ options, correct, onChange }) {
  // Display items in correct order for editing; correct[] stores original indices
  const orderedItems = correct.length === options.length
    ? correct.map(i => options[i])
    : [...options];

  const updateItem = (idx, val) => {
    const next = orderedItems.map((o, i) => i === idx ? val : o);
    onChange(next, next.map((_, i) => i));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...orderedItems];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next, next.map((_, i) => i));
  };

  const moveDown = (idx) => {
    if (idx === orderedItems.length - 1) return;
    const next = [...orderedItems];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next, next.map((_, i) => i));
  };

  return (
    <div className="qe-field">
      <label className="qe-label">Items <span className="qe-hint">— arrange in correct order (top = first)</span></label>
      <div className="qe-options-list">
        {orderedItems.map((item, idx) => (
          <div key={idx} className="qe-option-row">
            <span className="qe-order-num">{idx + 1}</span>
            <input className="qe-option-input" type="text" value={item}
              onChange={e => updateItem(idx, e.target.value)} placeholder={`Item ${idx + 1}`} />
            <button className="qe-move-btn" onClick={() => moveUp(idx)} disabled={idx === 0}>↑</button>
            <button className="qe-move-btn" onClick={() => moveDown(idx)} disabled={idx === orderedItems.length - 1}>↓</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Legacy KC / non-qbank question editor ────────────────────────────────
function LegacyQuestionEditor({ question, setQuestion }) {
  if (!question) return null;

  const handleOptionTextChange = (idx, newText) => {
    setQuestion(prev => ({
      ...prev,
      options: prev.options.map((o, i) => i === idx ? { ...o, text: newText } : o),
    }));
  };

  const handleOptionCorrectToggle = (idx) => {
    setQuestion(prev => {
      const isSingle = prev.interaction_type === 'single';
      return {
        ...prev,
        options: prev.options.map((o, i) => ({
          ...o,
          correct: isSingle ? (i === idx) : (i === idx ? !o.correct : o.correct),
        })),
      };
    });
  };

  return (
    <div>
      <div className="qe-field">
        <label className="qe-label">Question Text</label>
        <textarea className="qe-textarea" value={question.question_text || ''}
          onChange={e => setQuestion(prev => ({ ...prev, question_text: e.target.value }))}
          rows={3} placeholder="Enter your question..." />
      </div>

      <div className="qe-field qe-row">
        <label className="qe-label">Answer Type</label>
        <select className="qe-select" value={question.interaction_type}
          onChange={e => setQuestion(prev => ({ ...prev, interaction_type: e.target.value }))}>
          <option value="single">Single choice (pick one)</option>
          <option value="multiple">Multiple choice (pick all correct)</option>
        </select>
      </div>

      <div className="qe-field">
        <div className="qe-options-header">
          <label className="qe-label">Options</label>
          <button className="qe-add-btn" onClick={() => setQuestion(prev => ({ ...prev, options: [...prev.options, { text: '', correct: false }] }))}>+ Add option</button>
        </div>
        <div className="qe-options-list">
          {(question.options || []).map((opt, idx) => (
            <div key={idx} className={`qe-option-row ${opt.correct ? 'correct' : ''}`}>
              <button className={`qe-correct-toggle ${opt.correct ? 'active' : ''}`} onClick={() => handleOptionCorrectToggle(idx)}>
                {question.interaction_type === 'single' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    {opt.correct && <circle cx="12" cy="12" r="5" fill="currentColor"/>}
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={opt.correct ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="3"/>
                    {opt.correct && <polyline points="7 12 11 16 17 8"/>}
                  </svg>
                )}
              </button>
              <input className="qe-option-input" type="text" value={opt.text}
                onChange={e => handleOptionTextChange(idx, e.target.value)} placeholder={`Option ${idx + 1}`} />
              {opt.correct && <span className="qe-correct-label">✓ Correct</span>}
              {question.options.length > 2 && (
                <button className="qe-remove-btn" onClick={() => setQuestion(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }))}>✕</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
